import { assert } from "@animech/core-utilities";
import {
	ControllerBase,
	ControllerEvent,
	ControllerInterface,
	ControllerObject,
	CursorStyle,
	DragPickingPolicy,
	isControllerObject,
	SceneBuilder,
} from "@animech/axiom-scene-builder";
import { Entity, GraphNode, Layer, StandardMaterial } from "playcanvas";
import { createXZSpriteEntity } from "@animech/axiom-scene-utilities";
import { Root } from "@definitions";
import { DropOperation, DropOperationFactory } from "src/core/drag-and-drop";
import { SceneObject } from "src/common";
import { findParent, isEntity, isMobile } from "src/utility";

export class DragAndDropController extends ControllerBase<Root> {
	private _dropOperationFactory: DropOperationFactory<SceneObject>;

	private _iconMaterial: StandardMaterial;
	private _dropOperation?: DropOperation;
	private _releaseRenderRequest?: () => void;
	private _sceneBuilder: SceneBuilder<Root>;
	private _entity?: Entity;

	constructor(
		iconMaterial: StandardMaterial,
		sceneBuilder: SceneBuilder<Root>,
		dropOperationFactory: DropOperationFactory<SceneObject>,
	) {
		super();
		this._iconMaterial = iconMaterial;
		this._sceneBuilder = sceneBuilder;
		this._dropOperationFactory = dropOperationFactory;
	}

	get hoverCursorStyle(): CursorStyle {
		return "move";
	}

	get dragCursorStyle(): CursorStyle {
		return "grabbing";
	}

	/** Movement and drop use pointer coordinates only, so only the start needs a GPU hit. */
	get dragPicking(): DragPickingPolicy {
		return "start-only";
	}

	/**
	 * Cancels an in-progress drop through the operation's `dragLeave`, which puts
	 * the product and its attachments back. A drop whose start failed has nothing
	 * to cancel.
	 */
	onDragInvalidated(): void {
		const operation = this._dropOperation;
		this._dropOperation = undefined;
		const release = this._releaseRenderRequest;
		this._releaseRenderRequest = undefined;
		try {
			// The render request is held only once dragEnter has started the
			// operation. A dragLeave to one that never entered would assert and hide
			// the error that aborted the start.
			if (release) {
				operation?.onEvent({ type: "dragLeave" });
			}
		} finally {
			release?.();
		}
	}

	isHovered(event: ControllerEvent): boolean {
		const parent = this._entity?.parent;
		const meshNode = event.mesh?.node;
		if (!parent || !meshNode || !parent.isAncestorOf(meshNode)) {
			return false;
		}
		const controllerEntity = findParent(meshNode, this._isControllerEntity);
		return !controllerEntity || controllerEntity === this._entity;
	}

	canHandle(event: ControllerEvent): boolean {
		const isDragging = !!this._dropOperation;

		if (isMobile()) {
			return false;
		}

		if (event.type === "dragStart") {
			return !isDragging;
		} else if (event.type === "dragMove" || event.type === "dragEnd") {
			return isDragging;
		} else {
			return false;
		}
	}

	onEvent(
		event: ControllerEvent,
		object: ControllerObject<Root>,
		controllerInterface: ControllerInterface<Root>,
	): boolean {
		const { x, y } = event;
		const targetObject = object.targetObject;
		assert(
			targetObject,
			`Controller object with name "${object.name}" has no target`,
		);
		const { pushAutoRenderRequest, popAutoRenderRequest } = controllerInterface;

		if (event.type === "dragStart") {
			if (this._dropOperation) {
				return true;
			}
			this._dropOperation = this._dropOperationFactory(targetObject);
			if (!this._dropOperation) {
				return true;
			}
			const finished = this._dropOperation.onEvent({
				type: "dragEnter",
				x,
				y,
			});
			if (finished) {
				this._dropOperation = undefined;
				return true;
			}

			pushAutoRenderRequest();
			this._releaseRenderRequest = popAutoRenderRequest;
		} else if (event.type === "dragMove") {
			if (!this._dropOperation) {
				return true;
			}
			const finished = this._dropOperation.onEvent({
				type: "dragOver",
				x,
				y,
			});
			if (finished) {
				const release = this._releaseRenderRequest;
				this._releaseRenderRequest = undefined;
				release?.();
				this._dropOperation = undefined;
				return true;
			}
		} else if (event.type === "dragEnd") {
			if (!this._dropOperation) {
				return true;
			}
			const finished = this._dropOperation.onEvent({
				type: "drop",
			});
			if (finished) {
				const release = this._releaseRenderRequest;
				this._releaseRenderRequest = undefined;
				release?.();
				this._dropOperation = undefined;
				return true;
			}
		}
		return false;
	}

	updateEntity(_object: SceneObject, entity: Entity, layer: Layer): void {
		if (this.modelEntities.length === 0) {
			const sprite = createXZSpriteEntity(this._iconMaterial, 1 / 1000, layer);
			// Manipulation gizmos are transient UI and must never reach the AR/GLB
			// export (their icon sprites use unnamed runtime textures that crash the
			// exporter). Tag the model's mesh-instance nodes too — the sprite plane
			// lives on the model component's internal graph node, which the exporter
			// visits independently, and the GLB export filters on mesh-instance node
			// tags rather than entity tags.
			sprite.tags.add("excludeFromAR");
			for (const mi of sprite.model?.meshInstances ?? []) {
				mi.node.tags.add("excludeFromAR");
			}
			const parent = new Entity();
			parent.tags.add("excludeFromAR");
			parent.addChild(sprite);
			parent.setLocalScale(0.5, 0.5, 0.5);
			entity.addChild(parent);
			this.modelEntities.push(sprite);
		}
		this._entity = entity;
	}

	private _isControllerEntity = (node: GraphNode): node is Entity => {
		const object = isEntity(node) && this._sceneBuilder.objectMap.get(node);
		return !!object && isControllerObject(object);
	};
}
